// Doc 07 §6 / issue #188 — Template editor: the trip detail page, minus what a template
// has no notion of. It renders through the same `ListTopbar`, `ListTabs`, `Toolbar`,
// `PackingList`, `TodoList` and `NotesTab` the trip page uses (a template supplies a
// `ParticipantContext` built from its travelers, which is all those components read),
// so the two screens stay in step by construction rather than by copying.
//
// The differences, all from issue #188:
//   - no progress cards: a template has no progress to show.
//   - no "templates applied" chip: templates can't be nested.
//   - checkboxes only STAGE. A template's rows are staged for its travelers, and an
//     import carries that staging onto the trip rows it creates ("this bin is already
//     packed"). Nothing here is ever "packed".
//   - to-dos have no checkbox at all — staging is a packing-item concept, and there is
//     nothing to complete until the template has been imported. They keep the relative
//     due offset (Doc 07 §6) in place of a calendar date.
//   - the share dialog separates the people with *access* to the template from its
//     travelers.

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AvatarStack, Button, MetaChip, TEMPLATE_EMOJIS, Toast, vars } from "@pack-it-up/ui-web";
import {
  compareByOrder,
  isOk,
  type CategoryId,
  type FractionalIndex,
  type ItemId,
  type NoteId,
  type ParticipantId,
  type TodoId,
  type UserId,
} from "@pack-it-up/shared";
import { createShortcutRegistry, errorToMessage, type Neighbors } from "@pack-it-up/viewmodel";
import {
  applyNotesView,
  applyTodoView,
  applyView,
  categoryKind,
  collectTags,
  participantContextOf,
  participantsOf,
  relevantParticipants,
  type Assignment,
  type Category,
  type ListViewConfig,
  type MemberEntry,
  type NotesViewConfig,
  type PackingItem,
  type ParticipantRow,
  type RelativeOffset,
  type TemplateSummary,
  type TodoItem,
} from "@pack-it-up/core";
import type {
  createCategoriesStore,
  createItemsStore,
  createNotesStore,
  createTemplatesStore,
  createTodosStore,
} from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { ShareIcon, SettingsIcon } from "../../app/icons.js";
import { useShortcuts } from "../../lib/useShortcuts.js";
import { ListTabs, isListTab, type ListTab } from "../ListTabs.js";
import { ListTopbar } from "../shared/ListTopbar.js";
import { DeleteCategoryDialog } from "../trip/DeleteCategoryDialog.js";
import { NotesTab } from "../trip/NotesTab.js";
import { PackingList } from "../trip/PackingList.js";
import { TodoList } from "../trip/TodoList.js";
import { Toolbar } from "../trip/Toolbar.js";
import type { ItemComposerDraft } from "../trip/ItemComposer.js";
import type { ItemPatchDraft, TodoPatchDraft } from "../trip/RowEditForm.js";
import { StagingCheckbox } from "./StagingCheckbox.js";
import { TemplateTravelers } from "./TemplateTravelers.js";
import { TemplateDeleteDialog } from "./TemplateDeleteDialog.js";
import { TemplateSettingsDialog } from "./TemplateSettingsDialog.js";
import { RemoveParticipantDialog } from "../../features/participants/RemoveParticipantDialog.js";
import { ShareDialogHost } from "../../features/share/ShareDialogHost.js";

export interface TemplateEditorProps {
  template: TemplateSummary;
  categoriesStore: ReturnType<typeof createCategoriesStore>;
  itemsStore: ReturnType<typeof createItemsStore>;
  todosStore: ReturnType<typeof createTodosStore>;
  notesStore: ReturnType<typeof createNotesStore>;
  templatesStore: ReturnType<typeof createTemplatesStore>;
  onDeleted: () => void;
  // Issue #8 — opens the "duplicate this template" prompt, owned by the page. Offered
  // to viewers too: copying a template shared read-only with you is the point.
  onDuplicate: () => void;
}

// How long the post-confirm Undo toast stays actionable before the delete executes.
const UNDO_WINDOW_MS = 6000;

const defaultView = (): ListViewConfig => ({
  sort: "manual",
  completedToBottom: false,
  filterAssignees: "all",
  filterStatus: "all",
  query: "",
  filterTags: [],
});

const defaultNotesView = (): NotesViewConfig => ({ sort: "manual", filterTags: [], query: "" });

export const TemplateEditor = ({
  template,
  categoriesStore,
  itemsStore,
  todosStore,
  notesStore,
  templatesStore,
  onDeleted,
  onDuplicate,
}: TemplateEditorProps): ReactNode => {
  const navigate = useNavigate();
  const { auth } = useStores();
  const { friends: friendsStore, pendingTemplateDelete } = useSession();
  const authUser = useStoreValue(auth, (s) => s.user);
  const { tab: tabParam } = useParams();
  const tab: ListTab = isListTab(tabParam) ? tabParam : "packing";
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Issue #185 — removing a traveler, same two-step shape: a confirm dialog stating what
  // it costs in rows, then a deferred execution behind an Undo toast.
  const [removeTarget, setRemoveTarget] = useState<{ id: ParticipantId; name: string } | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{ id: ParticipantId; name: string } | null>(null);
  const removeTimer = useRef<{ timer: ReturnType<typeof setTimeout>; run: () => void } | null>(null);
  // Category pending deletion (from the section header's ⋮ menu) — confirmed via dialog.
  const [deleteCat, setDeleteCat] = useState<Category | null>(null);
  // Row delete (item/todo), same deferred shape as the delete/removal above — no
  // confirm dialog, the row just vanishes and the actual repo delete waits behind an
  // Undo toast.
  const [pendingItemDelete, setPendingItemDelete] = useState<{ id: ItemId; name: string } | null>(null);
  const itemDeleteTimer = useRef<{ timer: ReturnType<typeof setTimeout>; itemId: ItemId } | null>(null);
  const [pendingTodoDelete, setPendingTodoDelete] = useState<{ id: TodoId; name: string } | null>(null);
  const todoDeleteTimer = useRef<{ timer: ReturnType<typeof setTimeout>; todoId: TodoId } | null>(null);
  // The toolbar's view state. The trip page keeps its equivalent in `tripDetailStore`;
  // a template's editor drives its stores directly, so it holds the two configs itself
  // and runs the same `applyView`/`applyTodoView` over them.
  const [itemsView, setItemsView] = useState<ListViewConfig>(defaultView);
  const [todosView, setTodosView] = useState<ListViewConfig>(defaultView);
  const [notesView, setNotesView] = useState<NotesViewConfig>(defaultNotesView);

  const rawCategories = useStoreValue(categoriesStore, (s) => s.categories());
  const rawItems = useStoreValue(itemsStore, (s) => s.items());
  const rawTodos = useStoreValue(todosStore, (s) => s.todos());
  const notes = useStoreValue(notesStore, (s) => s.notes);
  // Friends are the pool of real accounts a template traveler can name (issue #185).
  const friends = useStoreValue(friendsStore, (s) => s.friends);

  // T29: the template's own tag palette, same union as the trip's `tagPalette()`.
  const tagPalette = useMemo(() => collectTags([...rawItems, ...rawTodos, ...notes]), [rawItems, rawTodos, notes]);

  const canEdit = template.role === "owner" || template.role === "editor";
  const actor = (authUser?.id ?? "") as UserId;

  // Issue #185/#188 — the template's default travelers, in the shape the shared list
  // components read. `allowProxyCompletion` is on inside it: a template has no "me", so
  // an editor stages rows on any traveler's behalf.
  const participants = template.participants ?? {};
  const list = useMemo(() => participantContextOf(participants, actor), [participants, actor]);
  const participantIds = useMemo(() => participantsOf(list), [list]);

  // Categories are typed per tab; one still shows on a tab while it holds that tab's
  // rows, so a legacy row under an untyped category stays reachable (same rule as trips).
  const packingCategories = useMemo(
    () => rawCategories.filter((c) => categoryKind(c) === "packing" || rawItems.some((i) => i.categoryId === c.id)),
    [rawCategories, rawItems],
  );
  const todoCategories = useMemo(
    () => rawCategories.filter((c) => categoryKind(c) === "todo" || rawTodos.some((t) => t.categoryId === c.id)),
    [rawCategories, rawTodos],
  );

  // The same view pipeline the trip page applies — over the staging map, since that is
  // what "done" means on a template row.
  const visibleItems = useMemo(
    () => applyView([...rawItems].sort(compareByOrder), itemsView, participantIds, "staging"),
    [rawItems, itemsView, participantIds],
  );
  const visibleTodos = useMemo(
    () => applyTodoView([...rawTodos].sort(compareByOrder), todosView, participantIds),
    [rawTodos, todosView, participantIds],
  );
  const visibleNotes = useMemo(
    () => applyNotesView([...notes].sort(compareByOrder), notesView),
    [notes, notesView],
  );

  // Keyboard shortcuts: one registry per editor view, as on the trip page. The bindings
  // themselves are registered by the list components that own the state they act on
  // (Esc cancels a row edit; cmd+enter starts an add on the first category).
  const shortcutRegistry = useRef(createShortcutRegistry());
  const registry = shortcutRegistry.current;
  useShortcuts(registry);

  const toast = (msg: string): void => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  // ── Row commands (one store call each, same shape as TripDetailPage) ──────────
  const addItem = (categoryId: CategoryId, draft: ItemComposerDraft): void => {
    void (async () => {
      const description = draft.notes.trim();
      const result = await itemsStore.getState().create({
        categoryId,
        name: draft.name.trim(),
        quantity: draft.quantity,
        ...(description !== "" ? { description } : {}),
        ...(draft.tags.length > 0 ? { tags: draft.tags } : {}),
      });
      if (isOk(result) && draft.assignment.kind !== "everyone") {
        void itemsStore.getState().setAssignment(result.value, draft.assignment);
      }
    })();
  };

  const addTodo = (categoryId: CategoryId, draft: ItemComposerDraft): void => {
    void (async () => {
      const description = draft.notes.trim();
      const result = await todosStore.getState().create({
        categoryId,
        name: draft.name.trim(),
        ...(description !== "" ? { description } : {}),
        ...(draft.tags.length > 0 ? { tags: draft.tags } : {}),
      });
      if (!isOk(result)) return;
      if (draft.assignment.kind !== "everyone") {
        void todosStore.getState().setAssignment(result.value, draft.assignment);
      }
      // A template to-do is due relative to the trip's start date, never on a calendar
      // date — the composer's due field returns an offset here.
      if (draft.relativeDueOffset != null) {
        void todosStore.getState().update({ todoId: result.value, relativeDueOffset: draft.relativeDueOffset });
      }
    })();
  };

  const addCategory = (kind: "packing" | "todo") => (name: string, emoji?: string): void => {
    void categoriesStore
      .getState()
      .create({ name: name.trim(), parentId: template.id, kind, ...(emoji !== undefined ? { emoji } : {}) })
      .then((r) => {
        if (isOk(r)) toast(`Added category "${name}"`);
      });
  };

  // Cascade delete (confirmed): the category's items and todos, then the category.
  const deleteCategory = (cat: Category): void => {
    void (async () => {
      await Promise.all([
        ...rawItems.filter((i) => i.categoryId === cat.id).map((i) => itemsStore.getState().remove(i.id)),
        ...rawTodos.filter((t) => t.categoryId === cat.id).map((t) => todosStore.getState().remove(t.id)),
      ]);
      await categoriesStore.getState().remove(cat.id);
    })();
  };

  // Deferred row delete: the row vanishes immediately (filtered out of `visibleItems`
  // below), but the repo delete only fires once the Undo window elapses.
  const deleteItem = (itemId: ItemId): void => {
    const item = rawItems.find((i) => i.id === itemId);
    if (item === undefined) return;
    const pending = itemDeleteTimer.current;
    if (pending !== null) {
      clearTimeout(pending.timer);
      void itemsStore.getState().remove(pending.itemId);
    }
    const timer = setTimeout(() => {
      itemDeleteTimer.current = null;
      setPendingItemDelete(null);
      void itemsStore.getState().remove(itemId);
    }, UNDO_WINDOW_MS);
    itemDeleteTimer.current = { timer, itemId };
    setPendingItemDelete({ id: itemId, name: item.name });
  };
  const undoDeleteItem = (): void => {
    if (itemDeleteTimer.current !== null) clearTimeout(itemDeleteTimer.current.timer);
    itemDeleteTimer.current = null;
    setPendingItemDelete(null);
  };
  const deleteTodo = (todoId: TodoId): void => {
    const todo = rawTodos.find((t) => t.id === todoId);
    if (todo === undefined) return;
    const pending = todoDeleteTimer.current;
    if (pending !== null) {
      clearTimeout(pending.timer);
      void todosStore.getState().remove(pending.todoId);
    }
    const timer = setTimeout(() => {
      todoDeleteTimer.current = null;
      setPendingTodoDelete(null);
      void todosStore.getState().remove(todoId);
    }, UNDO_WINDOW_MS);
    todoDeleteTimer.current = { timer, todoId };
    setPendingTodoDelete({ id: todoId, name: todo.name });
  };
  const undoDeleteTodo = (): void => {
    if (todoDeleteTimer.current !== null) clearTimeout(todoDeleteTimer.current.timer);
    todoDeleteTimer.current = null;
    setPendingTodoDelete(null);
  };

  // A click on a row's box moves the whole row: stage every traveler it is for, or
  // unstage them all when they already are. A `shared` row is a single collapsed record,
  // so one call covers it (applyToggle's shared branch).
  const toggleRowStaging = (item: PackingItem): void => {
    const store = itemsStore.getState();
    if (item.assignment.kind === "shared") {
      void store.toggleStaged(item.id, actor as ParticipantId, true);
      return;
    }
    const targets = relevantParticipants(item, list);
    const allStaged = targets.length > 0 && targets.every((pid) => item.staging?.byParticipant[pid]?.done === true);
    for (const pid of targets) {
      const staged = item.staging?.byParticipant[pid]?.done === true;
      if (staged === !allStaged) continue; // already where this click is taking the row
      void store.toggleStaged(item.id, pid, list.members[pid]?.kind === "user");
    }
  };

  const toggleParticipantStaging = (itemId: string, target: ParticipantId, targetIsUser: boolean): void => {
    void itemsStore.getState().toggleStaged(itemId as ItemId, target, targetIsUser);
  };

  // ── Traveler removal + template delete (deferred, behind an Undo toast) ───────
  const participantRows = useMemo(
    (): ParticipantRow[] => [
      ...rawItems.map((item: PackingItem) => ({ collection: "items" as const, item })),
      ...rawTodos.map((item: TodoItem) => ({ collection: "todos" as const, item })),
    ],
    [rawItems, rawTodos],
  );

  // The rows as of *now* — read when a removal runs, not when it was armed: a plan
  // built from a stale list could patch a row another write has since changed.
  const currentRows = (): ParticipantRow[] => [
    ...itemsStore.getState().items().map((item: PackingItem) => ({ collection: "items" as const, item })),
    ...todosStore.getState().todos().map((item: TodoItem) => ({ collection: "todos" as const, item })),
  ];

  // Only one removal can be pending: arming a second flushes the first immediately,
  // since the new toast replaces the Undo affordance that was the first one's point.
  const flushPendingRemoval = (): void => {
    const pending = removeTimer.current;
    if (pending === null) return;
    clearTimeout(pending.timer);
    removeTimer.current = null;
    pending.run();
  };

  const handleRemoveTraveler = (): void => {
    const target = removeTarget;
    if (target === null) return;
    setRemoveTarget(null);
    flushPendingRemoval();
    setPendingRemoval(target);
    const run = (): void => void templatesStore.getState().removeParticipant(template.id, target.id, currentRows());
    const timer = setTimeout(() => {
      removeTimer.current = null;
      setPendingRemoval(null);
      run();
    }, UNDO_WINDOW_MS);
    removeTimer.current = { timer, run };
  };

  const undoRemoveTraveler = (): void => {
    if (removeTimer.current !== null) clearTimeout(removeTimer.current.timer);
    removeTimer.current = null;
    setPendingRemoval(null);
    toast("Removal cancelled");
  };

  // Doc 10 §5: archiving is reversible, so it fires straight from the menu — no confirm.
  // Navigate immediately rather than waiting on the write to round-trip — same reason
  // as delete below: the local cache drops this template from `home.templates` the
  // instant the update is issued, and this page reads the template from that list.
  const handleArchive = (): void => {
    navigate("/templates");
    void templatesStore.getState().archiveTemplate(template.id);
  };

  // Confirm = arm the session-scoped Undo (pendingTemplateDelete, shown by AppShell —
  // this page is gone before the window elapses) and navigate immediately. Waiting on
  // the write, here or on the Undo window, would leave the user staring at this page
  // after `home.templates`' optimistic update has already dropped the template from
  // it — the "Loading…" fallback below with nothing to undo it.
  const handleDeleteTemplate = async (): Promise<void> => {
    setShowDeleteDialog(false);
    pendingTemplateDelete.getState().arm(template.id, template.name);
    onDeleted();
  };

  // Members map for the share dialog and note author names: TemplateSummary doesn't
  // carry the full members map (only my role), so surface at least the signed-in
  // user; the sharing store still drives invites/links/cap logic for the template.
  const shareMembers = useMemo((): Record<string, MemberEntry> => {
    if (authUser === null) return {};
    return {
      [authUser.id]: {
        kind: "user" as const,
        role: template.role,
        profile: { displayName: authUser.displayName, email: authUser.email },
        settings: { stagingOptIn: false },
      },
    };
  }, [authUser, template.role]);

  // The travelers section, rendered inside the share dialog (issue #188). The dialog is
  // where "who is involved" is decided, and this is the half of it that answers "who is
  // going on the trip" rather than "who may edit this template".
  const travelers = useCallback(
    (): ReactNode => (
      <TemplateTravelers
        participants={participants}
        friends={friends}
        canEdit={canEdit}
        onAddManaged={(displayName) => void templatesStore.getState().addManagedParticipant(template.id, displayName)}
        onAddUser={(friend) =>
          void templatesStore.getState().addUserParticipant(template.id, {
            userId: friend.userId,
            displayName: friend.displayName,
            email: friend.email,
            ...(friend.photoUrl !== undefined ? { photoUrl: friend.photoUrl } : {}),
          })
        }
        onRemove={(participantId) =>
          setRemoveTarget({
            id: participantId,
            name: participants[participantId]?.displayName ?? "this traveler",
          })
        }
      />
    ),
    [participants, friends, canEdit, templatesStore, template.id],
  );

  const people = participantIds.map((id) => ({ name: list.members[id]?.profile.displayName ?? "?" }));
  const assigneeOptions = participantIds.map((id) => ({
    value: id as UserId,
    label: list.members[id]?.profile.displayName ?? "?",
  }));
  const tabCounts: Record<ListTab, number> = { packing: rawItems.length, todos: rawTodos.length, notes: notes.length };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <ListTopbar
        name={template.name}
        nameLabel="Template name"
        canEdit={canEdit}
        isViewer={template.role === "viewer"}
        onRename={(name) => void templatesStore.getState().renameTemplate(template.id, name)}
        menuLabel="More options for this template"
        menuItems={[
          { label: "Duplicate template", onClick: onDuplicate },
          ...(canEdit
            ? [
                { label: "Archive template", onClick: handleArchive },
                { label: "Delete template", onClick: () => setShowDeleteDialog(true), danger: true },
              ]
            : []),
        ]}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShareOpen(true)}>
              {ShareIcon}
              Share
            </Button>
            <Button variant="ghost" size="sm" aria-label="Template settings" onClick={() => setSettingsOpen(true)}>
              {SettingsIcon}
            </Button>
          </>
        }
      />

      {/* Travellers chip — clicking opens the share dialog, where a template's travelers
          are managed alongside the people it is shared with. */}
      <header style={{ padding: `${vars.space.lg} ${vars.space.lg} 0` }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: vars.space.md, marginBottom: vars.space.md }}>
          <button
            type="button"
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            onClick={() => setShareOpen(true)}
            aria-label="Manage template travellers"
          >
            <MetaChip clickable peopleChip icon={<AvatarStack people={people} size="xs" />}>
              {people.length} traveller{people.length === 1 ? "" : "s"}
            </MetaChip>
          </button>
        </div>
      </header>

      <ListTabs basePath={`/template/${template.id}`} active={tab} counts={tabCounts} />

      <div style={{ padding: `${vars.space.md} ${vars.space.lg} ${vars.space.xl}` }}>
        {tab === "packing" && (
          <>
            <Toolbar
              view={itemsView}
              onChange={(patch) => setItemsView((v) => ({ ...v, ...patch }))}
              assignees={assigneeOptions}
              tags={tagPalette}
              statusLabels={{ incomplete: "Not staged", complete: "Staged" }}
              searchPlaceholder="Search items…"
              summary={`${rawItems.length} item${rawItems.length === 1 ? "" : "s"} across ${packingCategories.length} categor${packingCategories.length === 1 ? "y" : "ies"}`}
            />
            <PackingList
              categories={packingCategories}
              items={visibleItems.filter((i) => i.id !== pendingItemDelete?.id)}
              list={list}
              actor={actor}
              canEdit={canEdit}
              sort={itemsView.sort}
              registry={registry}
              tagPalette={tagPalette}
              field="staging"
              renderCompletion={(item) => (
                <StagingCheckbox
                  item={item}
                  participants={relevantParticipants(item, list)}
                  canEdit={canEdit}
                  onToggle={() => toggleRowStaging(item)}
                />
              )}
              onToggle={toggleParticipantStaging}
              onToggleCollapse={(categoryId) => void categoriesStore.getState().toggleCollapsed(categoryId)}
              onAddItem={addItem}
              onAddCategory={addCategory("packing")}
              onUpdateCategory={(categoryId, patch) => void categoriesStore.getState().update({ categoryId, ...patch })}
              onDeleteCategory={setDeleteCat}
              onUpdateItem={(itemId: ItemId, patch: ItemPatchDraft) => void itemsStore.getState().update({ itemId, ...patch })}
              onSetAssignment={(itemId: string, assignment: Assignment) =>
                void itemsStore.getState().setAssignment(itemId as ItemId, assignment)
              }
              onResetToImport={() => undefined}
              onDeleteItem={deleteItem}
              onMoveItem={(rowId: string, toCategoryId: CategoryId, neighbors: Neighbors) =>
                void itemsStore.getState().move(rowId as ItemId, toCategoryId, neighbors)
              }
              onReorderCategory={(categoryId: CategoryId, order: FractionalIndex) =>
                void categoriesStore.getState().reorder(categoryId, order)
              }
            />
          </>
        )}

        {tab === "todos" && (
          <>
            <Toolbar
              view={todosView}
              onChange={(patch) => setTodosView((v) => ({ ...v, ...patch }))}
              tags={tagPalette}
              statusLabels={{ incomplete: "To do", complete: "Done" }}
              searchPlaceholder="Search to-dos…"
              summary={`${rawTodos.length} to-do${rawTodos.length === 1 ? "" : "s"} across ${todoCategories.length} categor${todoCategories.length === 1 ? "y" : "ies"}`}
            />
            <TodoList
              categories={todoCategories}
              todos={visibleTodos.filter((t) => t.id !== pendingTodoDelete?.id)}
              list={list}
              actor={actor}
              canEdit={canEdit}
              sort={todosView.sort}
              registry={registry}
              tagPalette={tagPalette}
              showCompletion={false}
              dueMode="relative"
              onSetRelativeDue={(todoId: TodoId, offset: RelativeOffset | null) =>
                void todosStore.getState().update({ todoId, relativeDueOffset: offset })
              }
              onToggle={() => undefined}
              onToggleCollapse={(categoryId) => void categoriesStore.getState().toggleCollapsed(categoryId)}
              onAddTodo={addTodo}
              onAddCategory={addCategory("todo")}
              onUpdateCategory={(categoryId, patch) => void categoriesStore.getState().update({ categoryId, ...patch })}
              onDeleteCategory={setDeleteCat}
              onUpdateTodo={(todoId: TodoId, patch: TodoPatchDraft) => void todosStore.getState().update({ todoId, ...patch })}
              onPinDueDate={() => undefined}
              onSetAssignment={(todoId: TodoId, assignment: Assignment) =>
                void todosStore.getState().setAssignment(todoId, assignment)
              }
              onDeleteTodo={deleteTodo}
              onMoveTodo={(rowId: string, toCategoryId: CategoryId, neighbors: Neighbors) =>
                void todosStore.getState().move(rowId as TodoId, toCategoryId, neighbors)
              }
              onReorderCategory={(categoryId: CategoryId, order: FractionalIndex) =>
                void categoriesStore.getState().reorder(categoryId, order)
              }
            />
          </>
        )}

        {tab === "notes" && (
          <NotesTab
            notes={visibleNotes}
            canEdit={canEdit}
            currentUserId={authUser?.id}
            members={shareMembers}
            view={notesView}
            onViewChange={(patch) => setNotesView((v) => ({ ...v, ...patch }))}
            tags={tagPalette}
            tagPalette={tagPalette}
            onAdd={async (body, tags) => {
              const r = await notesStore.getState().add(body, tags);
              if (!r.ok) {
                toast(errorToMessage(r.error));
                return false;
              }
              return true;
            }}
            onUpdate={(noteId, body) => void notesStore.getState().updateBody(noteId as NoteId, body)}
            onUpdateTags={(noteId, tags) => void notesStore.getState().updateTags(noteId as NoteId, tags)}
            onReorder={(noteId, order) => void notesStore.getState().move(noteId as NoteId, order)}
            onRemove={(noteId) => void notesStore.getState().remove(noteId as NoteId)}
          />
        )}
      </div>

      {deleteCat !== null && (
        <DeleteCategoryDialog
          category={deleteCat}
          count={
            rawItems.filter((i) => i.categoryId === deleteCat.id).length +
            rawTodos.filter((t) => t.categoryId === deleteCat.id).length
          }
          onClose={() => setDeleteCat(null)}
          onConfirm={() => deleteCategory(deleteCat)}
        />
      )}

      {/* Delete dialog — plain confirm; delete never touches a trip. */}
      {showDeleteDialog && (
        <TemplateDeleteDialog
          templateName={template.name}
          onConfirm={handleDeleteTemplate}
          onClose={() => setShowDeleteDialog(false)}
        />
      )}

      {/* Issue #185 — what removing this traveler costs in rows, before committing. */}
      {removeTarget !== null && (
        <RemoveParticipantDialog
          displayName={removeTarget.name}
          resourceKind="template"
          preview={templatesStore.getState().previewParticipantRemoval(removeTarget.id, participantRows)}
          onConfirm={handleRemoveTraveler}
          onClose={() => setRemoveTarget(null)}
        />
      )}

      {/* Unified share dialog (Doc 08 §8) — the same component the trip page uses, plus
          the travelers section only a template has. Also opened by viewers (the
          traveller chip), so stays mounted for every role; `canManage` tells the store
          whether to subscribe to the owner/editor-only shareLinks and pendingInvites
          streams. */}
      <ShareDialogHost
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        resource={{ kind: "template", id: template.id }}
        resourceName={template.name}
        members={shareMembers}
        canManage={canEdit}
        travelers={travelers()}
      />

      <TemplateSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        templateColor={{
          value: template.color,
          onChange: (color) => void templatesStore.getState().setTemplateColor(template.id, color),
          canEdit,
        }}
        templateEmoji={{
          value: template.emoji ?? TEMPLATE_EMOJIS[0]!,
          onChange: (emoji) => void templatesStore.getState().setTemplateEmoji(template.id, emoji),
          canEdit,
        }}
      />

      {/* Pending traveler removal (issue #185) — same deferred-execution Undo. */}
      {pendingRemoval !== null && (
        <div style={{ position: "fixed", bottom: vars.space.lg, left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
          <Toast message={`Removing ${pendingRemoval.name}…`} onUndo={undoRemoveTraveler} />
        </div>
      )}

      {/* Deferred row delete (item/todo) — same Undo shape as the two above. */}
      {pendingItemDelete !== null && (
        <div style={{ position: "fixed", bottom: vars.space.lg, left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
          <Toast message={`Deleting "${pendingItemDelete.name}"…`} onUndo={undoDeleteItem} />
        </div>
      )}
      {pendingTodoDelete !== null && (
        <div style={{ position: "fixed", bottom: vars.space.lg, left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
          <Toast message={`Deleting "${pendingTodoDelete.name}"…`} onUndo={undoDeleteTodo} />
        </div>
      )}

      {toastMsg !== null && <Toast message={toastMsg} />}
    </div>
  );
};
